import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import InfoModal from './common/InfoModal';
import CustomParkingLocationModal from './CustomParkingLocationModal';
import api from '../services/api';
import { cachedGet } from '../utils/lookupCache';

// Parking badge color options with contrasting text colors
export const PARKING_COLORS = [
  { id: 1, name: 'Red', hex: '#e6194B', darkHex: '#ff6b6b', textColor: '#ffffff', darkTextColor: '#000000' },
  { id: 2, name: 'Green', hex: '#3cb44b', darkHex: '#69db7c', textColor: '#ffffff', darkTextColor: '#000000' },
  { id: 3, name: 'Yellow', hex: '#ffe119', darkHex: '#ffe066', textColor: '#000000', darkTextColor: '#000000' },
  { id: 4, name: 'Blue', hex: '#4363d8', darkHex: '#748ffc', textColor: '#ffffff', darkTextColor: '#000000' },
  { id: 5, name: 'Orange', hex: '#f58231', darkHex: '#ffa94d', textColor: '#000000', darkTextColor: '#000000' },
  { id: 6, name: 'Purple', hex: '#911eb4', darkHex: '#da77f2', textColor: '#ffffff', darkTextColor: '#000000' },
  { id: 7, name: 'Cyan', hex: '#42d4f4', darkHex: '#66d9e8', textColor: '#000000', darkTextColor: '#000000' },
  { id: 8, name: 'Magenta', hex: '#f032e6', darkHex: '#f783ac', textColor: '#ffffff', darkTextColor: '#000000' },
  { id: 9, name: 'Lime', hex: '#bfef45', darkHex: '#c0eb75', textColor: '#000000', darkTextColor: '#000000' },
  { id: 10, name: 'Pink', hex: '#fabed4', darkHex: '#fcc2d7', textColor: '#000000', darkTextColor: '#000000' },
  { id: 11, name: 'Teal', hex: '#469990', darkHex: '#63e6be', textColor: '#ffffff', darkTextColor: '#000000' },
  { id: 12, name: 'Lavender', hex: '#dcbeff', darkHex: '#e5dbff', textColor: '#000000', darkTextColor: '#000000' },
  { id: 13, name: 'Brown', hex: '#9A6324', darkHex: '#d9925a', textColor: '#ffffff', darkTextColor: '#000000' },
  { id: 14, name: 'Beige', hex: '#fffac8', darkHex: '#fff9db', textColor: '#000000', darkTextColor: '#000000' },
  { id: 15, name: 'Maroon', hex: '#800000', darkHex: '#c92a2a', textColor: '#ffffff', darkTextColor: '#ffffff' },
  { id: 16, name: 'Mint', hex: '#aaffc3', darkHex: '#b2f2bb', textColor: '#000000', darkTextColor: '#000000' },
  { id: 17, name: 'Olive', hex: '#808000', darkHex: '#a9e34b', textColor: '#ffffff', darkTextColor: '#000000' },
  { id: 18, name: 'Apricot', hex: '#ffd8b1', darkHex: '#ffd8a8', textColor: '#000000', darkTextColor: '#000000' },
  { id: 19, name: 'Navy', hex: '#000075', darkHex: '#4c6ef5', textColor: '#ffffff', darkTextColor: '#ffffff' },
];

export const getParkingColorById = (id: number | null, isDarkMode: boolean = false): string | null => {
  if (!id) return null;
  const color = PARKING_COLORS.find(c => c.id === id);
  return color ? (isDarkMode ? color.darkHex : color.hex) : null;
};

export const getParkingColorStyles = (id: number | null, isDarkMode: boolean = false): { backgroundColor: string; color: string } | null => {
  if (!id) return null;
  const color = PARKING_COLORS.find(c => c.id === id);
  if (!color) return null;
  return {
    backgroundColor: isDarkMode ? color.darkHex : color.hex,
    color: isDarkMode ? color.darkTextColor : color.textColor,
  };
};

interface Location {
  recno: number;
  lname: string;
  locationtype: string;
  ladd: string;
  ladd2: string;
  lcity: string;
  lstate: string;
  lzip: string;
  coordinates: string | null;
}

interface ParkingLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: string;
  driverName: string;
  currentParkingloc: number | null;
  currentParkingName: string;
  currentParkingColor: number | null;
  onSave: () => void;
}

const ParkingLocationModal: React.FC<ParkingLocationModalProps> = ({
  isOpen,
  onClose,
  driver,
  driverName,
  currentParkingloc,
  currentParkingName,
  currentParkingColor,
  onSave,
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoadFailed, setLocationsLoadFailed] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);

  // Ref for debounced search timeout
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Detect dark mode
  const isDarkMode = document.documentElement.classList.contains('dark');

  const filteredLocations = locations.filter(loc =>
    loc.lname.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/safety/parking-location', {
        driver,
        parkingloc: selectedLocation,
        parking_color: selectedColor,
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving parking location:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await api.post('/safety/parking-location', {
        driver,
        parkingloc: null,
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Error clearing parking location:', error);
    } finally {
      setSaving(false);
    }
  };

  const fetchLocations = async () => {
    setLoading(true);
    setLocationsLoadFailed(false);
    try {
      const response = await cachedGet<unknown[]>('/admin/locations');
      setLocations((response.data as never[]) || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocationsLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change with debounced fetch (CLAUDE.md pattern)
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);

    // Clear any pending search
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    // Trigger debounced fetch if query is long enough
    if (value.length >= 1) {
      searchTimeoutRef.current = setTimeout(() => {
        fetchLocations();
        setHasSearched(true);
      }, 300);
    }
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedLocation(currentParkingloc);
      setSelectedColor(currentParkingColor);
      setSearchQuery('');
      setLocations([]);
      setHasSearched(false);
      // Clear any pending search from previous session
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    }
  }, [isOpen, currentParkingloc, currentParkingColor]);

  const handleCustomLocationCreated = async (newRecno: number) => {
    // Save the new location as the driver's parking location
    try {
      await api.post('/safety/parking-location', {
        driver,
        parkingloc: newRecno,
      });
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving parking location:', error);
      // If save fails, still refresh and select so user can manually save
      await fetchLocations();
      setSelectedLocation(newRecno);
    }
  };

  const selectedLocationData = selectedLocation
    ? locations.find(l => l.recno === selectedLocation)
    : null;

  const selectedLocationName = selectedLocationData?.lname || (selectedLocation ? currentParkingName : null);

  // Parse coordinates and generate Google Maps URL
  const getGoogleMapsUrl = (coordinates: string | null): string | null => {
    if (!coordinates) return null;
    // Handle multiple coordinate pairs separated by semicolons or newlines
    const firstCoordPair = coordinates.split(/[\r\n;]+/)[0].trim();
    const parts = firstCoordPair.split(',').map(s => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      // Coordinates are in lat,lng format
      return `https://www.google.com/maps?q=${parts[0]},${parts[1]}`;
    }
    return null;
  };

  const mapsUrl = selectedLocationData?.coordinates ? getGoogleMapsUrl(selectedLocationData.coordinates) : null;

  // Alt+S saves, Alt+C closes — both mirroring the footer buttons' saving guard.
  // The nested custom-location and info modals open later, so they claim the
  // layer above this one and win while they're up.
  useAltHotkeys(
    { S: saving ? null : handleSave, C: saving ? null : onClose },
    undefined,
    { layer: isOpen },
  );

  return (
    <>
    <InfoModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Parking Location - ${driver} ${driverName}`}
      maxWidth="2xl"
      body={
        <div className="space-y-4">
          {selectedLocationName && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Current: </span>
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{selectedLocationName}</span>
              </div>
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 text-xs font-medium rounded bg-ok text-ok-fg hover:bg-green-200 dark:hover:bg-green-800"
                >
                  View On Map
                </a>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-label">
                Select Parking Location
              </label>
              <button
                type="button"
                onClick={() => setShowCustomModal(true)}
                className="px-3 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-800"
              >
                + Custom Location
              </button>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search locations..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3"
            />
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-80 overflow-y-auto">
              {!hasSearched && !searchQuery ? (
                <div className="p-4 text-center text-gray-500">Type to search locations...</div>
              ) : loading ? (
                <div className="p-4 text-center text-gray-500">Loading locations...</div>
              ) : filteredLocations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {locationsLoadFailed
                    ? 'Couldn\'t load locations — the request failed. Close and reopen to retry.'
                    : 'No locations found'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredLocations.map((location) => (
                    <label
                      key={location.recno}
                      className={`flex items-center p-3 cursor-pointer transition-colors ${
                        selectedLocation === location.recno
                          ? 'bg-blue-50 dark:bg-blue-900/30'
                          : 'hover:bg-highlight'
                      }`}
                    >
                      <input
                        type="radio"
                        name="parking-location"
                        checked={selectedLocation === location.recno}
                        onChange={() => setSelectedLocation(location.recno)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-heading">
                          {location.lname}
                        </div>
                        <div className="text-xs text-muted">
                          {location.locationtype && `${location.locationtype} • `}
                          {[location.ladd, location.ladd2].filter(Boolean).join(' ')}
                          {location.lcity && `, ${location.lcity}, ${location.lstate} ${location.lzip}`}
                        </div>
                      </div>
                      {getGoogleMapsUrl(location.coordinates) && (
                        <a
                          href={getGoogleMapsUrl(location.coordinates)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="ml-2 px-2 py-1 text-xs font-medium rounded bg-ok text-ok-fg hover:bg-green-200 dark:hover:bg-green-800"
                        >
                          Map
                        </a>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Color Picker */}
          <div>
            <label className="text-sm font-medium text-label mb-2 block">
              Badge Color{hoveredColor ? ` - ${hoveredColor}` : (selectedColor ? ` - ${PARKING_COLORS.find(c => c.id === selectedColor)?.name}` : '')}
            </label>
            <div
              className="grid border border-gray-300 dark:border-gray-600"
              style={{ gridTemplateColumns: `repeat(${PARKING_COLORS.length}, 1fr)` }}
            >
              {PARKING_COLORS.map((color, index) => (
                <button
                  key={color.id}
                  type="button"
                  onClick={() => setSelectedColor(selectedColor === color.id ? null : color.id)}
                  onMouseEnter={() => setHoveredColor(color.name)}
                  onMouseLeave={() => setHoveredColor(null)}
                  className={`h-8 flex items-center justify-center text-xs font-bold transition-all ${
                    index < PARKING_COLORS.length - 1 ? 'border-r border-gray-300 dark:border-gray-600' : ''
                  } ${
                    selectedColor === color.id
                      ? 'ring-2 ring-inset ring-black dark:ring-white'
                      : ''
                  }`}
                  style={{
                    backgroundColor: isDarkMode ? color.darkHex : color.hex,
                    color: isDarkMode ? color.darkTextColor : color.textColor,
                  }}
                >
                  B
                </button>
              ))}
            </div>
          </div>
        </div>
      }
      buttons={[
        ...(currentParkingloc ? [{
          label: 'Clear',
          onClick: handleClear,
          variant: 'danger' as const,
          disabled: saving
        }] : []),
        {
          label: 'Cancel',
          onClick: onClose,
          variant: 'secondary' as const
        },
        {
          label: saving ? 'Saving...' : 'Save',
          onClick: handleSave,
          variant: 'primary' as const,
          disabled: saving || (selectedLocation === null && selectedColor === null)
        }
      ]}
    />
    <CustomParkingLocationModal
      isOpen={showCustomModal}
      onClose={() => setShowCustomModal(false)}
      driver={driver}
      driverName={driverName}
      onSave={handleCustomLocationCreated}
    />
    </>
  );
};

export default ParkingLocationModal;
